import React, { useState, useEffect } from "react";
import {
  Select,
  Input,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Card,
  List,
  Avatar,
  Typography,
  Pagination,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  FolderOutlined,
  EditOutlined,
  DeleteTwoTone,
  ExclamationCircleOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import CaSideNav from "../../../../components/AdminComponents/CaSideNav";
import {
  CaAdminMenu,
  SecondaryNavDocustore,
} from "../../../../components/AdminComponents/MenuItems";
import SecondaryNav2 from "../../../../components/AdminComponents/SecondaryNav2";
import { useSelector } from "react-redux";
import { capitalizeWords } from "../../../../components/icons/ApplicationColor";
import CreateFolderModal from "./components/CreateFolderModal";
import AddDocumentModal from "./components/AddDocumentModal";
import CommonTopDetails from "../../../../components/AdminComponents/CommonTopDetails";

import axiosInstance from "../../../../api/Axios";
import "../../formStyles.css";
import "./DocustoreStyles.css";

const { Text } = Typography;

const { Option } = Select;

const DocustoreEmployees = () => {
  // State management
  const userInfo = useSelector((state) => state.user);

  function getCookie(name) {
    const cookies = document.cookie.split("; ");
    for (let i = 0; i < cookies.length; i++) {
      const [cookieName, cookieValue] = cookies[i].split("=");
      if (cookieName === name) {
        return cookieValue;
      }
    }
    return null;
  }

  const [selectedCountry, setSelectedCountry] = useState("");
  const [folders, setFolders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [parentFolderForModal, setParentFolderForModal] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set(['trainery', 'organization']));
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [isDeleteDocumentModalOpen, setIsDeleteDocumentModalOpen] =
    useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [createFolderForm] = Form.useForm();
  const [addDocumentForm] = Form.useForm();
  const [apiRoles, setApiRoles] = useState([]);
  const [apiCountries, setApiCountries] = useState([]);
  const [documentsData, setDocumentsData] = useState([]);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [canAdd, setCanAdd] = useState(true);
  const [organizationCategoryId, setOrganizationCategoryId] = useState(null);

  // Effects
  useEffect(() => {
    // const defaultCountry = userInfo?.data?.organization?.country || "United States";
    // setSelectedCountry(defaultCountry);
    fetchFolderData();
  }, [userInfo]);

  // API Functions
  const fetchFolderData = async () => {
    if (!userInfo?.data?.organization?.id || !userInfo?.data?.id) return;

    try {
      console.log(userInfo.data.organization.id);
      console.log(userInfo.data.id);

      setIsLoading(true);
      const response = await axiosInstance.get(
        `docustor/listorganizationtemplates/?orgnization_id=${userInfo.data.organization.id}&user_id=${userInfo.data.id}`
      );

      if (response.data.status && response.data.results?.length > 0) {
        const result = response.data.results[0];

        // Set countries from API response
        if (Array.isArray(result.countries)) {
          setApiCountries(result.countries);
          setSelectedCountry(result.countries[0] || "United States");
        } else if (result.country) {
          setApiCountries([result.country]);
          setSelectedCountry(result.country);
        }

        // Set roles from API response
        if (result.roles) {
          setApiRoles(result.roles);
        }

        // Set can_add permission
        setCanAdd(result.can_add !== false);

        // Set organization category ID
        if (result.organization_folder?.category) {
          setOrganizationCategoryId(result.organization_folder.category);
        }

        // Transform API data to component format
        const folderGroups = {
          trainery: {
            name: result.trainery_folder?.name || 'Trainery Folders',
            folders: [],
            can_document_add: result.trainery_folder?.can_document_add
          },
          organization: {
            name: result.organization_folder?.name || 'Organization Folders',
            folders: [],
            can_document_add: result.organization_folder?.can_document_add
          }
        };

        // Add trainery folders
        if (result.trainery_folder?.folders) {
          result.trainery_folder.folders.forEach((folder) => {
            folderGroups.trainery.folders.push({
              id: folder.id,
              name: folder.folder_name,
              description: folder.description,
              folder_id: folder.folder_id,
              can_edit: folder.can_edit,
              can_delete: folder.can_delete,
              can_document_add: result.trainery_folder.can_document_add,
              parentId: null,
              type: "trainery",
              createdAt: new Date().toLocaleDateString(),
            });
          });
        }

        // Add organization folders
        if (result.organization_folder?.folders) {
          result.organization_folder.folders.forEach((folder) => {
            folderGroups.organization.folders.push({
              id: folder.id,
              name: folder.folder_name,
              description: folder.description,
              folder_id: folder.folder_id,
              can_edit: folder.can_edit || folder["can_edit:"],
              can_delete: folder.can_delete,
              can_document_add: result.organization_folder.can_document_add,
              parentId: null,
              type: "organization",
              createdAt: new Date().toLocaleDateString(),
            });
          });
        }

        setFolders(folderGroups);

        // Auto-select first folder
        const allFolders = [...folderGroups.trainery.folders, ...folderGroups.organization.folders];
        if (allFolders.length > 0) {
          const firstFolder = allFolders[0];
          setSelectedFolder(firstFolder);
          fetchFolderDocuments(firstFolder.id);
        }
      }
    } catch (error) {
      console.error("Error fetching folder data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Event handlers
  const handleCreateFolder = async (formValues) => {
    setIsLoading(true);
    try {
      if (editingFolder) {
        // Update existing folder
        const folderData = {
          folder_name: formValues.folderName,
          description: formValues.description,
          organization: userInfo?.data?.organization?.id,
          level: 1,
          parent: null,
          category: organizationCategoryId,
        };

        const response = await axiosInstance.put(
          `/docustor/folder/${editingFolder.id}/`,
          folderData,
          {
            headers: {
              "X-CSRFToken": getCookie("csrftoken"),
            },
          }
        );

        if (response.data) {
          await fetchFolderData();
        }
      } else {
        // Create new folder
        const folderData = {
          folder_name: formValues.folderName,
          description: formValues.description,
          organization: userInfo?.data?.organization?.id,
          level: 1,
          parent: null,
          category: organizationCategoryId,
        };

        const response = await axiosInstance.post(
          "/docustor/folder/",
          folderData,
          {
            headers: {
              "X-CSRFToken": getCookie("csrftoken"),
            },
          }
        );

        if (response.data) {
          await fetchFolderData();
        }
      }
      handleCloseModal();
    } catch (error) {
      console.error(
        editingFolder ? "Error updating folder:" : "Error creating folder:",
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (parentFolder = null) => {
    if (!canAdd) return;
    setEditingFolder(null);
    setParentFolderForModal(parentFolder);
    setIsCreateModalOpen(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setIsCreateModalOpen(true);
  };

  const handleDeleteFolder = (folder) => {
    setFolderToDelete(folder);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.delete(
        `/docustor/folder/${folderToDelete.id}/`,
        {
          headers: {
            "X-CSRFToken": getCookie("csrftoken"),
          },
        }
      );

      if (response.status === 204 || response.status === 200) {
        await fetchFolderData();
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setFolderToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingFolder(null);
    setParentFolderForModal(null);
    createFolderForm.resetFields();
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setFolderToDelete(null);
  };

  const handleCountryChange = (country) => setSelectedCountry(country);

  const handleSearchChange = (event) => setSearchQuery(event.target.value);

  const fetchFolderDocuments = async (folderId, page = 1, size = 10) => {
    try {
      setDocumentsLoading(true);
      const response = await axiosInstance.get(
        `/docustor/folderdocuments/${folderId}/?page=${page}&page_size=${size}&status=True`
      );

      if (response.data.results) {
        setDocumentsData(response.data.results);
        setDocumentsCount(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching folder documents:", error);
      setDocumentsData([]);
      setDocumentsCount(0);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder);
    setCurrentPage(1);
    fetchFolderDocuments(folder.id, 1, pageSize);
  };

  const onDocumentsPageChange = (page, size) => {
    setCurrentPage(page);
    if (selectedFolder) {
      fetchFolderDocuments(selectedFolder.id, page, size);
    }
  };

  const onDocumentsPageSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
    if (selectedFolder) {
      fetchFolderDocuments(selectedFolder.id, 1, size);
    }
  };

  const handleAddDocument = async (formValues) => {
    setIsLoading(true);
    try {
      // Transform additionalFields array to key-value JSON object
      const additionalFieldsJson = {};
      if (formValues.additionalFields && Array.isArray(formValues.additionalFields)) {
        formValues.additionalFields.forEach(field => {
          if (field.key && field.type) {
            additionalFieldsJson[field.key] = field.type;
          }
        });
      }

      if (editingDocument) {
        // Update existing document
        const documentData = {
          folder_id: selectedFolder.id,
          type_name: formValues.documentTitle,
          allowed_extensions: Array.isArray(formValues.documentType) ? formValues.documentType : [formValues.documentType || "pdf"],
          max_file_size_mb: formValues.maxFileSize || 10,
          verification_required: formValues.verificationRequired || false,
          mandatory: formValues.mandatory || false,
          issue_date_required: false,
          expiry_date_required: false,
          status: true,
          organization: userInfo?.data?.organization?.id,
          additional_fields: additionalFieldsJson,
        };

        const response = await axiosInstance.put(
          `/docustor/documenttype/${editingDocument.id}/`,
          documentData,
          {
            headers: {
              "X-CSRFToken": getCookie("csrftoken"),
            },
          }
        );

        if (response.data) {
          await fetchFolderDocuments(selectedFolder.id, currentPage, pageSize);
        }
      } else {
        // Create new document
        const documentData = {
          folder_id: selectedFolder.id,
          type_name: formValues.documentTitle,
          allowed_extensions: Array.isArray(formValues.documentType) ? formValues.documentType : [formValues.documentType || "pdf"],
          max_file_size_mb: formValues.maxFileSize || 10,
          verification_required: formValues.verificationRequired || false,
          mandatory: formValues.mandatory || false,
          issue_date_required: false,
          expiry_date_required: false,
          status: true,
          organization: userInfo?.data?.organization?.id,
          additional_fields: additionalFieldsJson,
        };

        const response = await axiosInstance.post(
          "/docustor/documenttype/",
          documentData,
          {
            headers: {
              "X-CSRFToken": getCookie("csrftoken"),
            },
          }
        );

        if (response.data) {
          await fetchFolderDocuments(selectedFolder.id, currentPage, pageSize);
        }
      }
      setIsAddDocumentModalOpen(false);
      setEditingDocument(null);
      addDocumentForm.resetFields();
    } catch (error) {
      console.error(
        editingDocument ? "Error updating document:" : "Error adding document:",
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAddDocumentModal = () => {
    setIsAddDocumentModalOpen(false);
    setEditingDocument(null);
    addDocumentForm.resetFields();
  };

  const handleEditDocument = (document) => {
    if (document.can_edit === false) return;
    setEditingDocument(document);
    
    // Transform additional_fields JSON object back to array format
    const additionalFieldsArray = [];
    if (document.additional_fields && typeof document.additional_fields === 'object') {
      Object.entries(document.additional_fields).forEach(([key, type]) => {
        additionalFieldsArray.push({ key, type });
      });
    }
    
    addDocumentForm.setFieldsValue({
      documentTitle: document.title,
      documentType: document.extensions || [],
      maxFileSize: document.max_file_size_mb,
      mandatory: document.mandatory,
      verificationRequired: document.verification_required,
      additionalFields: additionalFieldsArray,
    });
    setIsAddDocumentModalOpen(true);
  };

  const handleDeleteDocument = (document) => {
    if (document.can_delete === false) return;
    setDocumentToDelete(document);
    setIsDeleteDocumentModalOpen(true);
  };

  const handleConfirmDeleteDocument = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.delete(
        `/docustor/documenttype/${documentToDelete.id}/`,
        {
          headers: {
            "X-CSRFToken": getCookie("csrftoken"),
          },
        }
      );

      if (response.status === 204 || response.status === 200) {
        await fetchFolderDocuments(selectedFolder.id, currentPage, pageSize);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
    } finally {
      setIsLoading(false);
      setIsDeleteDocumentModalOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleCloseDeleteDocumentModal = () => {
    setIsDeleteDocumentModalOpen(false);
    setDocumentToDelete(null);
  };

  const getDocumentTypeIcon = (type) => {
    const iconStyle = { fontSize: "18px", marginRight: "8px" };
    switch (type?.toLowerCase()) {
      case "pdf":
        return <span style={{ ...iconStyle, color: "#d32f2f" }}>📄</span>;
      case "excel":
        return <span style={{ ...iconStyle, color: "#1976d2" }}>📊</span>;
      case "word":
        return <span style={{ ...iconStyle, color: "#1976d2" }}>📝</span>;
      case "powerpoint":
        return <span style={{ ...iconStyle, color: "#ff9800" }}>📊</span>;
      case "image":
        return <span style={{ ...iconStyle, color: "#4caf50" }}>🖼️</span>;
      case "text":
        return <span style={{ ...iconStyle, color: "#757575" }}>📄</span>;
      case "csv":
        return <span style={{ ...iconStyle, color: "#4caf50" }}>📋</span>;
      default:
        return <span style={{ ...iconStyle, color: "#757575" }}>📄</span>;
    }
  };

  const toggleFolderExpansion = (folderId) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Transform documents data
  const transformedDocuments =
    documentsData?.map((doc) => ({
      id: doc.id,
      title: doc.type_name,
      type: doc.allowed_extensions || ["pdf"],
      extensions: doc.allowed_extensions || ["pdf"],
      mandatory: doc.mandatory,
      verification_required: doc.verification_required,
      max_file_size_mb: doc.max_file_size_mb,
      issue_date_required: doc.issue_date_required,
      expiry_date_required: doc.expiry_date_required,
      can_edit: doc.can_edit,
      can_delete: doc.can_delete,
      additional_fields: doc.additional_fields,
      createdAt: new Date(doc.created_at).toLocaleDateString(),
    })) || [];

  const toggleGroupExpansion = (groupType) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupType)) {
        newSet.delete(groupType);
      } else {
        newSet.add(groupType);
      }
      return newSet;
    });
  };

  // Computed values
  const getFilteredFolders = (folderList) => {
    return folderList.filter(folder =>
      folder.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <CaSideNav props={CaAdminMenu}>
      <SecondaryNav2 menuItems={SecondaryNavDocustore} />
      <div className="form-container-2">
        <div>
          {/* Country Selection - Top Left */}
          <div className="antd-test-client docustore-country-section">
            <Row gutter={20} className="docustore-country-row">
              <Col xs={24} sm={12} md={3} lg={3} xl={3}>
                <Select
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  className="my-form-input-antd-test-without-border"
                >
                  {apiCountries.map((country) => (
                    <Option key={country} value={country}>
                      {country}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </div>

          {/* Folder Summary */}
          <div className="docustore-folder-summary">
            <div className="docustore-summary-content">
              <span className="docustore-summary-title">Folder Summary</span>
              <span className="docustore-summary-divider">|</span>
              <span className="docustore-summary-count">
                {(folders.trainery?.folders?.length || 0) + (folders.organization?.folders?.length || 0)} Folder(s)
              </span>
            </div>
          </div>

          {/* Main Content Area */}
          <div className=" docustore-main-content">
            {/* Left Section - 1/3 width */}
            <div className="docustore-left-section">
              <div className="docustore-left-card">
                {/* Search and Add */}
                <div className="docustore-search-add">
                  <Input
                    placeholder="Search for folder"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="search-input-latest docustore-search-input"
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleOpenModal}
                    className="small-add-btn"
                    disabled={!canAdd}
                  />
                </div>

                {/* Folder Groups */}
                <div className="docustore-folder-groups">
                  {Object.entries(folders).map(([groupType, groupData]) => {
                    const isGroupExpanded = expandedGroups.has(groupType);
                    const filteredGroupFolders = getFilteredFolders(groupData.folders);
                    
                    return (
                      <div key={groupType} style={{ marginBottom: '16px' }}>
                        {/* Group Header */}
                        <div 
                          onClick={() => toggleGroupExpansion(groupType)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: isGroupExpanded ? '8px' : '4px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <span style={{ color: '#666', fontSize: '12px', fontWeight: 'bold' }}>
                            {isGroupExpanded ? '▼' : '▶'}
                          </span>
                          <FolderOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                          <Text strong style={{ color: '#333', fontSize: '14px' }}>
                            {groupData.name}
                          </Text>
                        </div>
                        
                        {/* Group Folders */}
                        {isGroupExpanded && (
                          <List
                            className="docustore-folder-list"
                            itemLayout="vertical"
                            dataSource={filteredGroupFolders}
                            renderItem={(parentFolder) => {
                              const hasChildren = filteredGroupFolders.some(folder => folder.parentId === parentFolder.id);
                              const isExpanded = expandedFolders.has(parentFolder.id);

                              return (
                                <div key={parentFolder.id}>
                                  <div
                                    onClick={() => handleFolderClick(parentFolder)}
                                    style={{
                                      padding: "8px 12px",
                                      marginBottom: "4px",
                                      marginLeft: "20px",
                                      backgroundColor: selectedFolder?.id === parentFolder.id ? "#e6f7ff" : "transparent",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      transition: "background-color 0.2s",
                                      borderLeft: selectedFolder?.id === parentFolder.id ? "3px solid #1890ff" : "3px solid transparent"
                                    }}
                                    onMouseEnter={(e) => {
                                      if (selectedFolder?.id !== parentFolder.id) {
                                        e.target.style.backgroundColor = '#f5f5f5';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (selectedFolder?.id !== parentFolder.id) {
                                        e.target.style.backgroundColor = 'transparent';
                                      }
                                    }}
                                  >
                                    <FolderOutlined style={{ color: "#1890ff", fontSize: "16px" }} />
                                    <Text style={{ fontSize: "14px", fontWeight: "400", color: "#333", flex: 1 }}>
                                      {parentFolder.name}
                                    </Text>
                                    <div style={{ display: "flex", alignItems: "center", gap: "4px", opacity: 0.7 }}>
                                      {parentFolder.can_edit && canAdd && (
                                        <EditOutlined
                                          style={{ color: "#666", fontSize: "14px", cursor: "pointer" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditFolder(parentFolder);
                                          }}
                                        />
                                      )}
                                      {parentFolder.can_delete && canAdd && (
                                        <DeleteTwoTone
                                          style={{ fontSize: "14px", cursor: "pointer" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFolder(parentFolder);
                                          }}
                                          twoToneColor="#ff4d4f"
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }}
                            locale={{
                              emptyText: (
                                <div style={{ textAlign: "center", color: "#999", padding: "1rem", fontSize: "1.2rem" }}>
                                  No folders found
                                </div>
                              )
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Section - 2/3 width */}
            <div className="docustore-right-section">
              {selectedFolder ? (
                <div className="docustore-content-area">
                  <CommonTopDetails
                    title="Document Summary"
                    titleSuffix={`${documentsCount || 0} Document(s)`}
                    showAddButton={selectedFolder?.can_document_add === true}
                    addButtonProps={{
                      modalType: "PopUP",
                      modalProps: () => setIsAddDocumentModalOpen(true),
                    }}
                    searchPlaceholder="Search documents..."
                    marginPass="0px"
                    paddingInlinepass="0px"
                  />

                  {/* Document List */}
                  <div style={{ marginTop: "20px" }}>
                    {documentsLoading ? (
                      <div style={{ textAlign: "center", padding: "40px" }}>
                        <Text>Loading documents...</Text>
                      </div>
                    ) : transformedDocuments.length > 0 ? (
                      <div>
                        {/* Headers */}
                        <Row
                          style={{
                            padding: "12px 16px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px 8px 0 0",
                            fontWeight: "600",
                            borderBottom: "1px solid #d9d9d9",
                          }}
                        >
                          <Col span={6}>
                            <Text strong>Document Name</Text>
                          </Col>
                          <Col span={5}>
                            <Text strong>Document Type</Text>
                          </Col>
                          <Col span={4}>
                            <Text strong>Mandatory</Text>
                          </Col>
                          <Col span={5}>
                            <Text strong>Verification Required</Text>
                          </Col>
                          <Col span={4} style={{ textAlign: "right" }}>
                            <Text strong>Actions</Text>
                          </Col>
                        </Row>

                        {/* Document Rows */}
                        {transformedDocuments.map((document, index) => (
                          <Row
                            key={document.id}
                            style={{
                              padding: "16px",
                              borderBottom:
                                index === transformedDocuments.length - 1
                                  ? "none"
                                  : "1px solid #f0f0f0",
                              backgroundColor: "white",
                              borderRadius:
                                index === transformedDocuments.length - 1
                                  ? "0 0 8px 8px"
                                  : "0",
                            }}
                            align="middle"
                          >
                            <Col span={6}>
                              <Text>{document.title}</Text>
                            </Col>
                            <Col span={5}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "4px"
                                }}
                              >
                                {Array.isArray(document.extensions) ? (
                                  document.extensions.map((ext, idx) => (
                                    <span key={idx} style={{
                                      backgroundColor: "#f0f0f0",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      textTransform: "uppercase"
                                    }}>
                                      {ext}
                                    </span>
                                  ))
                                ) : (
                                  <Text style={{ textTransform: "uppercase" }}>
                                    {document.type}
                                  </Text>
                                )}
                              </div>
                            </Col>
                            <Col span={4}>
                              <Text
                                style={{
                                  color: document.mandatory
                                    ? "#52c41a"
                                    : "#faad14",
                                }}
                              >
                                {document.mandatory ? "Yes" : "No"}
                              </Text>
                            </Col>
                            <Col span={5}>
                              <Text
                                style={{
                                  color: document.verification_required
                                    ? "#52c41a"
                                    : "#faad14",
                                }}
                              >
                                {document.verification_required ? "Yes" : "No"}
                              </Text>
                            </Col>
                            <Col
                              span={4}
                              style={{
                                textAlign: "right",
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "8px",
                              }}
                            >
                              <EditOutlined
                                style={{
                                  color:
                                    document.can_edit !== false
                                      ? "#295bdc"
                                      : "#d9d9d9",
                                  fontSize: "16px",
                                  cursor:
                                    document.can_edit !== false
                                      ? "pointer"
                                      : "not-allowed",
                                }}
                                onClick={() => handleEditDocument(document)}
                                title="Edit Document"
                              />
                              <DeleteTwoTone
                                style={{
                                  fontSize: "16px",
                                  cursor:
                                    document.can_delete !== false
                                      ? "pointer"
                                      : "not-allowed",
                                }}
                                onClick={() => handleDeleteDocument(document)}
                                title="Delete Document"
                                twoToneColor={
                                  document.can_delete !== false
                                    ? "#eb2f96"
                                    : "#d9d9d9"
                                }
                              />
                            </Col>
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#999",
                        }}
                      >
                        No documents found in this folder
                      </div>
                    )}

                    {/* Pagination */}
                    {transformedDocuments.length > 0 && (
                      <div
                        style={{
                          marginTop: "20px",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Pagination
                          current={currentPage}
                          pageSize={pageSize}
                          total={documentsCount}
                          showSizeChanger
                          showQuickJumper
                          // showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} documents`}
                          onChange={onDocumentsPageChange}
                          onShowSizeChange={onDocumentsPageSizeChange}
                          pageSizeOptions={["5", "10", "20", "50"]}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className="docustore-content-area"
                  style={{ textAlign: "center", paddingTop: "100px" }}
                >
                  <Text type="secondary" style={{ fontSize: "16px" }}>
                    Select a folder to view documents
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>

        <CreateFolderModal
          isVisible={isCreateModalOpen}
          onCancel={handleCloseModal}
          onSubmit={handleCreateFolder}
          loading={isLoading}
          form={createFolderForm}
          editingFolder={editingFolder}
          folders={folders}
          parentFolderForModal={parentFolderForModal}
          apiRoles={apiRoles}
          apiCountries={apiCountries}
          selectedCountry={selectedCountry}
        />

        <AddDocumentModal
          isOpen={isAddDocumentModalOpen}
          onClose={handleCloseAddDocumentModal}
          onSubmit={handleAddDocument}
          isLoading={isLoading}
          form={addDocumentForm}
          editingDocument={editingDocument}
        />

        {/* Delete Folder Confirmation Modal */}
        <Modal
          open={isDeleteModalOpen}
          centered
          width={400}
          title={
            <div>
              <ExclamationCircleOutlined className="docustore-delete-modal-title" />
              Delete Folder
            </div>
          }
          onCancel={handleCloseDeleteModal}
          footer={[
            <Button key="cancel" onClick={handleCloseDeleteModal}>
              Cancel
            </Button>,
            <Button
              key="confirm"
              type="primary"
              style={{
                background: "#f58029",
                fontWeight: "600",
              }}
              className="save-update-btn"
              onClick={handleConfirmDelete}
            >
              <DeleteTwoTone /> Delete
            </Button>,
          ]}
        >
          <p className="docustore-delete-modal-text">
            Are you sure you want to delete the folder{" "}
            <strong>{folderToDelete?.name}</strong>?
          </p>
        </Modal>

        {/* Delete Document Confirmation Modal */}
        <Modal
          open={isDeleteDocumentModalOpen}
          centered
          width={400}
          title={
            <div>
              <ExclamationCircleOutlined className="docustore-delete-modal-title" />
              Delete Document
            </div>
          }
          onCancel={handleCloseDeleteDocumentModal}
          footer={[
            <Button key="cancel" onClick={handleCloseDeleteDocumentModal}>
              Cancel
            </Button>,
            <Button
              key="confirm"
              type="primary"
              style={{
                background: "#f58029",
                fontWeight: "600",
              }}
              className="save-update-btn"
              onClick={handleConfirmDeleteDocument}
            >
              <DeleteTwoTone /> Delete
            </Button>,
          ]}
        >
          <p className="docustore-delete-modal-text">
            Are you sure you want to delete the document{" "}
            <strong>{documentToDelete?.title}</strong>?
          </p>
        </Modal>
      </div>
    </CaSideNav>
  );
};

export default DocustoreEmployees;
